import { Fragment, useState } from "react";
import { trpc } from "../../trpc";

import LoadingSpinner from "@client/components/utils/LoadingSpinner";

import { Button } from "@client/components/utils/Button";
import Modal from "@client/components/utils/Modal";
import useLoader from "@client/components/utils/useLoader";
import {
  ModalControls,
  useModalControls,
} from "@client/components/utils/useModalControls";
import type { UnconfirmedActionMessage } from "@engine/core";
import Container from "@lib/auth/client/Login/Container";
import Layout from "@lib/auth/client/Login/Layout";
import LoginNavbar from "@lib/auth/client/Login/LoginNavbar";
import {
  generateActionId,
  generateFunderId,
  generateProposalFormId,
  generateProposalId,
  generateRecommenderId,
  generateRoundId,
} from "@lib/utils/idGeneration";
import clsx from "clsx";
import { toast } from "react-toastify";
import { useLocation } from "wouter";

const getHomepageData = () => trpc().getHomepageData.query();

export function Home() {
  const addRoundModalControls = useModalControls();
  const result = useLoader(getHomepageData);

  if (result.type === "error") {
    return (
      <Layout>
        <LoginNavbar />
        <main>
          <Container>
            <div className="text-center">
              <h1 className="text-2xl font-bold">Error</h1>
              <p className="text-gray-500">
                An error occurred while loading the homepage. Please try
                refreshing the page.
              </p>
            </div>
          </Container>
        </main>
      </Layout>
    );
  } else if (result.type === "loading") {
    return (
      <Layout>
        <LoginNavbar />
        <main>
          <Container>
            <LoadingSpinner />
          </Container>
        </main>
      </Layout>
    );
  }

  const user = result.data.user;
  const userRounds = user?.participating_in ?? [];
  userRounds.sort((a, b) =>
    a.round.name.localeCompare(b.round.name, undefined, { numeric: true }),
  );
  userRounds.reverse();

  const canCreateRound = !!user;
  //const canCreateRound = state?.user?.isSfcAdmin;

  return (
    <Layout>
      <LoginNavbar />
      <main className="max-h-screen overflow-y-auto">
        <Container>
          <LandingPagePitch />

          {userRounds.length > 0 && (
            <TableWrapper>
              <TableHeader>
                <TableColumn width="100%">
                  <TableTitle text="Round Name" />
                </TableColumn>
              </TableHeader>
              {userRounds.map((recommender, idx: number) => (
                <Fragment key={idx}>
                  <TableRow isEven={idx % 2 === 0}>
                    <TableColumn width="100%">
                      <a
                        className="w-full"
                        href={`/round/${recommender.roundId}/`}
                      >
                        {recommender.round.name}
                      </a>
                    </TableColumn>
                  </TableRow>
                </Fragment>
              ))}
            </TableWrapper>
          )}

          <div className="mt-16 fit-content mx-auto">
            {canCreateRound && (
              <Button classes="mt-2" onClick={addRoundModalControls.openModal}>
                Create Round
              </Button>
            )}
          </div>
        </Container>
      </main>
      {canCreateRound && (
        <AddRoundModal modalControls={addRoundModalControls} userId={user.id} />
      )}
    </Layout>
  );
}

export function LandingPagePitch() {
  return (
    <div className="max-w-xl mx-auto">
      <p>
        The S-Process is a better way to allocate budgets with multiple proposal
        evaluators. It uses advanced voting theory to produce a correct result
        when multiple people are allocating a shared budget.{" "}
        <a
          className="light:text-blue-500 dark:text-blue-400"
          href="https://survivalandflourishing.fund/s-process"
        >
          Learn More
        </a>
      </p>
    </div>
  );
}

function AddRoundModal({
  modalControls,
  userId,
}: {
  modalControls: ModalControls;
  userId: string;
}) {
  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [, navigate] = useLocation();

  async function createRound(e: React.SyntheticEvent) {
    e.preventDefault();
    const roundId = generateRoundId();

    const roundCreated: UnconfirmedActionMessage = {
      aggregateId: roundId,
      id: generateActionId(),
      underlyingUserId: userId,
      actingAsUserId: userId,
      undoes: null,
      redoes: null,
      payload: {
        type: "CREATE_ROUND",
        name,
        defaultProposalFormId: generateProposalFormId(),
        holdProposalId: generateProposalId(),
        holdProposalName: "Hold",
        userId,
        initials,
        track: "NO_TRACK",
        initialFunderId: generateFunderId(),
        initialRecommenderId: generateRecommenderId(),
      },
    };

    await trpc()
      .performRoundAction.mutate(roundCreated)
      .then(() => {
        navigate(`/round/${roundId}/settings`);
      })
      .catch((e) => {
        toast.error("failed to create round: " + (e as Error).message);
      });
  }

  return (
    <Modal
      isOpen={modalControls.isModalOpen}
      onClose={modalControls.closeModal}
    >
      <form onSubmit={createRound}>
        <div className="text-center w-[600px] px-0 py-0">
          <div className="flex relative content-center items-center justify-around mb-4">
            <span className="text-lg">Create Round</span>
            <Button
              type="button"
              onClick={modalControls.closeModal}
              classes="absolute right-0"
            >
              Close
            </Button>
          </div>

          <div className="flex pb-2">
            <span className="w-[200px] text-right pr-2">Round Name:</span>
            <input
              value={name}
              type="text"
              className="border rounded-md w-[300px] px-1 light:text-black dark:text-white"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex pb-2">
            <span className="w-[200px] text-right pr-2">Creator Initials:</span>
            <input
              value={initials}
              type="text"
              className="border rounded-md w-[300px] px-1 light:text-black dark:text-white"
              onChange={(e) => setInitials(e.target.value)}
            />
          </div>

          <div className="flex pt-4 justify-around">
            <Button type="submit" onClick={(e) => void createRound(e)}>
              Create
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export function TableHeader({ children }: { children: React.ReactNode }) {
  return <div className="flex pt-4 pb-4 pl-4 md:px-6">{children}</div>;
}

export function TableWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={clsx(
        "w-full rounded-lg border mt-6 [&>*:last-child]:rounded-b-lg",
        "light:bg-[#f0f0f0] light:border-[#cecece]",
        "dark:bg-[#101010] dark:border-[#222222]",
      )}
    >
      {children}
    </div>
  );
}

export function TableColumn({
  width,
  center,
  children,
}: {
  width: string;
  center?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-center ${center ? "justify-center" : "justify-start"}`}
      style={{ width }}
    >
      {children}
    </div>
  );
}

function TableRow({
  selected,
  isEven,
  children,
}: {
  selected?: boolean;
  isEven: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        "flex items-center py-2 px-4 md:px-6 border-t cursor-pointer",
        "light:border-[#cecece] dark:border-[#222222]",
        selected
          ? isEven
            ? "light:bg-[#e1e9fc]"
            : "light:bg-[#346dee26]"
          : isEven
            ? "light:bg-white dark:bg-[#111111]"
            : "bg-transparent",
        "light:hover:bg-[#e1e9fc] light:hover:bg-[#346dee26]",
      )}
    >
      {children}
    </div>
  );
}

export function TableTitle({ text }: { text: string }) {
  return (
    <h4 className="text-xs leading-4 light:text-[#333333] tracking-widest m-0 font-inherit font-sans">
      {text}
    </h4>
  );
}
